import React, { useCallback } from 'react'
import type { ColumnId } from '../lib/constants'
import type { Tree, VisibleRow } from '../lib/tree'
import { useGanttStoreApi } from '../store/ganttStore'
import type { ScheduleActivity } from '../types'

export type GanttRowOp = 'delete' | 'insert' | 'indent' | 'outdent' | 'moveUp' | 'moveDown'

export interface GanttKeyboardDeps {
  rows: VisibleRow<ScheduleActivity>[]
  rowIndexById: Map<string, number>
  tree: Tree<ScheduleActivity>
  focusRow: (index: number, extend: boolean) => void
  startEdit: (id: string, column: ColumnId) => void
  /** Task 16 wires the row operations (Delete / Insert / Alt+Shift+arrows). */
  onRowOp?: (op: GanttRowOp) => void
  /** Task 3 (keyboard row entry): `Tab` on a focused row with no editor open jumps into its first
   * (or, with Shift, last) editable cell instead of leaving the grid. */
  startEditEdge?: (id: string, edge: 'first' | 'last') => void
  /** Task 3: `?` opens the shortcuts help dialog. */
  onShowShortcuts?: () => void
}

// Review fix (Important #7): the column-visibility menu (`GanttGridHeader`'s Radix `DropdownMenu`)
// is the one Radix portal whose React ancestor is the scroller div, so its own menu-navigation
// keys (ArrowUp/Down, Enter) bubble here too — without this guard, ArrowUp/Down moved the grid
// focus and Enter opened a cell editor behind the open menu. Same class of fix as `DateCell`'s
// keydown `stopPropagation` for the calendar popover, but this menu isn't inside a cell editor, so
// it needs its own guard here.
const isFormField = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null
  if (!el || !el.tagName) return false
  const tag = el.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable === true) return true
  return !!el.closest('[role="menu"], [role="menuitem"], [role="menuitemcheckbox"], [data-radix-popper-content-wrapper]')
}

/** Keyboard map of the grid (spec B §9.2). Acts on `focusedId`; ignored inside editors. */
export const useGanttKeyboard = ({ rows, rowIndexById, focusRow, startEdit, onRowOp, startEditEdge, onShowShortcuts }: GanttKeyboardDeps) => {
  const api = useGanttStoreApi()

  return useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isFormField(e.target)) return
    const st = api.getState()
    const focusedIndex = st.focusedId ? rowIndexById.get(st.focusedId) : undefined
    const current = focusedIndex == null ? -1 : focusedIndex
    const row = current >= 0 ? rows[current] : undefined

    if (e.altKey && e.shiftKey && onRowOp) {
      const op: GanttRowOp | null =
        e.key === 'ArrowRight' ? 'indent' : e.key === 'ArrowLeft' ? 'outdent' : e.key === 'ArrowUp' ? 'moveUp' : e.key === 'ArrowDown' ? 'moveDown' : null
      if (op) { e.preventDefault(); onRowOp(op); return }
    }

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        focusRow(current < 0 ? 0 : current - 1, e.shiftKey)
        return
      case 'ArrowDown':
        e.preventDefault()
        focusRow(current < 0 ? 0 : current + 1, e.shiftKey)
        return
      case 'Home':
        e.preventDefault()
        focusRow(0, e.shiftKey)
        return
      case 'End':
        e.preventDefault()
        focusRow(rows.length - 1, e.shiftKey)
        return
      case 'ArrowLeft': {
        e.preventDefault()
        if (!row) return
        if (row.hasChildren && !row.collapsed) { st.toggleCollapsed(row.node.activity.id); return }
        const parentId = row.node.parent ? row.node.parent.activity.id : null
        const parentIndex = parentId ? rowIndexById.get(parentId) : undefined
        if (parentIndex != null) focusRow(parentIndex, e.shiftKey)
        return
      }
      case 'ArrowRight': {
        e.preventDefault()
        if (!row) return
        if (row.hasChildren && row.collapsed) st.toggleCollapsed(row.node.activity.id)
        else if (row.hasChildren) focusRow(current + 1, e.shiftKey)
        return
      }
      case 'Tab':
        // Task 3 (keyboard row entry): jump straight into the row's first/last editable cell
        // instead of leaving the grid (the browser default). No focused row: let the browser move
        // focus as normal.
        if (!st.focusedId || !startEditEdge) return
        e.preventDefault()
        startEditEdge(st.focusedId, e.shiftKey ? 'last' : 'first')
        return
      case 'Enter':
      case 'F2':
        if (e.key === 'Enter' && e.shiftKey) {
          // Task 3: Shift+Enter on a focused row (no editor open) inserts a new activity below,
          // same as the `Insert` key.
          if (onRowOp) { e.preventDefault(); onRowOp('insert') }
          return
        }
        if (st.focusedId) { e.preventDefault(); startEdit(st.focusedId, 'name') }
        return
      case '?':
        // Task 3: shortcuts help dialog.
        if (onShowShortcuts) { e.preventDefault(); onShowShortcuts() }
        return
      case 'Escape':
        e.preventDefault()
        if (st.editingCell) st.setEditingCell(null)
        else st.clearSelection()
        return
      case '+':
      case '=':
        e.preventDefault()
        st.zoomStep(1)
        return
      case '-':
      case '_':
        e.preventDefault()
        st.zoomStep(-1)
        return
      case 'Delete':
      case 'Insert':
        if (onRowOp) { e.preventDefault(); onRowOp(e.key === 'Delete' ? 'delete' : 'insert') }
        return
      default:
    }
  }, [api, rows, rowIndexById, focusRow, startEdit, onRowOp, startEditEdge, onShowShortcuts])
}
